import DailyIframe from '@daily-co/daily-js';
// Daily.co uses createFrame as a function from the default export
import videoConfig from '../config/videoConfig';
import videoSecurityService from './videoSecurityService';

/**
 * Daily.co Video Service
 * Handles video room creation, joining, and management
 * 
 * Reliability: 95-99% (managed infrastructure)
 */

class DailyVideoService {
  constructor() {
    this.apiKey = videoConfig.daily.apiKey;
    this.domain = videoConfig.daily.domain;
    this.callFrame = null;
    this.currentRoom = null;
    this.participants = new Map();
    this.eventHandlers = new Map();
  }

  /**
   * Check if Daily.co is configured
   */
  isConfigured() {
    return !!this.apiKey;
  }

  /**
   * Create a video room for a session
   * SECURITY: Only admin/coach can create rooms. Students can only join existing rooms.
   * @param {string} sessionId - Session ID
   * @param {Object} options - Room options
   * @param {string} userType - User type (admin, coach, student)
   * @param {string} userId - User ID for logging
   * @returns {Promise<Object>} Room data with URL
   */
  async createRoom(sessionId, options = {}, userType = null, userId = null) {
    if (!this.isConfigured()) {
      throw new Error('Daily.co API key not configured. Please set REACT_APP_DAILY_API_KEY in .env file');
    }

    // SECURITY: Check if user can create room (role-based + rate limiting)
    if (userId && userType) {
      const permissionCheck = await videoSecurityService.canCreateRoom(userId, userType);
      if (!permissionCheck.allowed) {
        throw new Error(permissionCheck.reason || 'Unauthorized to create video room');
      }
    } else if (userType && userType !== 'admin' && userType !== 'coach') {
      // Fallback check if userId not provided
      throw new Error('Unauthorized: Only administrators and coaches can start video sessions. Students can only join existing sessions.');
    }

    try {
      // Sanitize room name: only allow alphanumeric, dashes, and underscores
      // Daily.co room names must be lowercase and can only contain: a-z, 0-9, -, _
      const sanitizedSessionId = sessionId.replace(/[^a-zA-Z0-9_-]/g, '-').toLowerCase();
      const roomName = `session-${sanitizedSessionId}`.substring(0, 128); // Max 128 chars

      // Get room properties from config
      const defaultSettings = videoConfig.daily.defaultRoomSettings || {};
      const roomProperties = {
        ...defaultSettings,
        ...options
      };

      const requestBody = {
        name: roomName,
        privacy: 'public', // Changed to public to allow direct URL access
        properties: roomProperties
      };

      const response = await fetch('https://api.daily.co/v1/rooms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        console.error('Daily.co API error:', errorData);
        throw new Error(errorData.error || errorData.message || `Failed to create room: ${response.status}`);
      }

      const room = await response.json();
      this.currentRoom = room;

      // SECURITY: Log room creation for monitoring
      if (userId && userType) {
        await videoSecurityService.logRoomCreation(userId, userType, sessionId, roomName).catch(err => {
          console.warn('Failed to log room creation:', err);
        });
      }

      return room;
    } catch (error) {
      console.error('Error creating Daily.co room:', error);
      throw error;
    }
  }

  /**
   * Get or create room for a session
   * SECURITY: Only admin/coach can create new rooms. Students can only join existing rooms.
   * @param {string} sessionId - Session ID
   * @param {string} userType - User type (admin, coach, student)
   * @param {string} userId - User ID for logging
   * @returns {Promise<Object>} Room data
   */
  async getOrCreateRoom(sessionId, userType = null, userId = null) {
    try {
      // Sanitize room name for lookup (same as in createRoom)
      const sanitizedSessionId = sessionId.replace(/[^a-zA-Z0-9_-]/g, '-').toLowerCase();
      const roomName = `session-${sanitizedSessionId}`.substring(0, 128);

      // Try to get existing room
      const response = await fetch(`https://api.daily.co/v1/rooms/${roomName}`, {
        headers: {
          'Authorization': `Bearer ${this.apiKey}`
        }
      });

      if (response.ok) {
        const room = await response.json();
        this.currentRoom = room;
        return room;
      }

      // Room doesn't exist (404)
      if (response.status === 404) {
        // SECURITY: Only admin/coach can create new rooms
        if (userType && userType !== 'admin' && userType !== 'coach') {
          throw new Error('No video session available. Please wait for an instructor to start the session.');
        }
        // Create room (only if user is admin/coach)
        return await this.createRoom(sessionId, {}, userType, userId);
      }

      // Other error, throw it
      const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
      throw new Error(errorData.error || errorData.message || `Failed to get room: ${response.status}`);
    } catch (error) {
      console.error('Error getting/creating room:', error);
      // Don't auto-create on network errors for students
      if (userType && userType !== 'admin' && userType !== 'coach') {
        throw new Error('Unable to join video session. Please contact your instructor.');
      }
      // For admin/coach, try to create on network error
      if (error.message.includes('404') || error.message.includes('Failed to fetch')) {
        if (userType === 'admin' || userType === 'coach') {
          return await this.createRoom(sessionId, {}, userType, userId);
        }
      }
      throw error;
    }
  }

  /**
   * Generate token for joining room
   * @param {string} roomName - Room name
   * @param {string} userId - User ID
   * @param {Object} tokenOptions - Token options (isOwner, isInstructor, etc.)
   * @returns {Promise<string>} Token
   */
  async generateToken(roomName, userId, tokenOptions = {}) {
    if (!this.isConfigured()) {
      throw new Error('Daily.co API key not configured');
    }

    try {
      const response = await fetch('https://api.daily.co/v1/meeting-tokens', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          properties: {
            room_name: roomName,
            user_id: userId,
            is_owner: tokenOptions.isOwner || false,
            enable_screenshare: tokenOptions.enableScreenshare !== false,
            enable_recording: tokenOptions.enableRecording || false,
            exp: Math.round(Date.now() / 1000) + (60 * 60 * 24) // 24 hours
          }
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate token');
      }

      const data = await response.json();
      return data.token;
    } catch (error) {
      console.error('Error generating token:', error);
      throw error;
    }
  }

  /**
   * Initialize Daily.co call frame
   * @param {HTMLElement|string} parentElementOrUrl - Parent element or room URL
   * @param {Object} options - Join options
   * @returns {Object} Call frame instance
   */
  initializeCallFrame(parentElementOrUrl, options = {}) {
    if (!this.isConfigured()) {
      throw new Error('Daily.co API key not configured');
    }

    try {
      // Destroy existing call frame if it exists
      if (this.callFrame) {
        try {
          this.callFrame.destroy();
        } catch (destroyError) {
          console.warn('Error destroying existing call frame:', destroyError);
        }
        this.callFrame = null;
      }

      // Create new call frame
      const callFrameOptions = {
        showLeaveButton: true,
        iframeStyle: {
          position: 'relative',
          width: '100%',
          height: '100%',
          border: '0',
          borderRadius: '8px'
        },
        ...options
      };

      // If parentElement is provided, use it; otherwise create without parent
      let callFrame;
      if (parentElementOrUrl && typeof parentElementOrUrl === 'object' && parentElementOrUrl.nodeType) {
        // Parent element provided
        callFrame = DailyIframe.createFrame(parentElementOrUrl, callFrameOptions);
      } else {
        // No parent element, create frame without immediate attachment
        callFrame = DailyIframe.createFrame(callFrameOptions);
      }

      this.callFrame = callFrame;
      return callFrame;
    } catch (error) {
      console.error('Error initializing call frame:', error);
      // If error is about duplicate instances, try to clean up and retry
      if (error.message && error.message.includes('Duplicate')) {
        console.warn('Duplicate instance detected, cleaning up...');
        this.destroy();
        // Retry once
        try {
          const callFrameOptions = {
            showLeaveButton: true,
            iframeStyle: {
              position: 'relative',
              width: '100%',
              height: '100%',
              border: '0',
              borderRadius: '8px'
            },
            ...options
          };
          const callFrame = parentElementOrUrl && typeof parentElementOrUrl === 'object' && parentElementOrUrl.nodeType
            ? DailyIframe.createFrame(parentElementOrUrl, callFrameOptions)
            : DailyIframe.createFrame(callFrameOptions);
          this.callFrame = callFrame;
          return callFrame;
        } catch (retryError) {
          throw new Error('Failed to create call frame after cleanup: ' + retryError.message);
        }
      }
      throw error;
    }
  }

  /**
   * Join a video room
   * @param {string} roomUrl - Room URL
   * @param {Object} joinOptions - Join options
   * @returns {Promise<void>}
   */
  async joinRoom(roomUrl, joinOptions = {}) {
    if (!this.callFrame) {
      throw new Error('Call frame not initialized. Call initializeCallFrame first.');
    }

    try {
      // Get room name from options or extract from URL
      let roomName = joinOptions.roomName;
      if (!roomName) {
        // Extract room name from URL (e.g., "https://domain.daily.co/room-name" -> "room-name")
        roomName = roomUrl.split('/').pop().split('?')[0];
      }
      
      // Generate token for joining (works for both public and private rooms)
      let token = null;
      try {
        const isOwner = joinOptions.userType === 'admin' || joinOptions.userType === 'coach';
        token = await this.generateToken(roomName, joinOptions.userId || 'anonymous', {
          isOwner: isOwner,
          enableScreenshare: true,
          enableRecording: false
        });
      } catch (tokenError) {
        console.warn('Failed to generate token, trying direct join:', tokenError);
        // If token generation fails, try direct join (for public rooms)
      }

      const defaultOptions = {
        userName: joinOptions.userName || 'Student',
        userData: {
          userId: joinOptions.userId,
          userType: joinOptions.userType || 'student'
        },
        startVideoOff: joinOptions.startVideoOff !== false,
        startAudioOff: joinOptions.startAudioOff !== false, // Muted by default for students
        ...joinOptions
      };

      // Join with token if available, otherwise use URL directly
      if (token) {
        await this.callFrame.join({
          token: token,
          ...defaultOptions
        });
      } else {
        await this.callFrame.join({
          url: roomUrl,
          ...defaultOptions
        });
      }

      this.currentRoom = { url: roomUrl };
    } catch (error) {
      console.error('Error joining room:', error);
      throw error;
    }
  }

  /**
   * Leave the current room
   */
  async leaveRoom() {
    if (this.callFrame) {
      try {
        await this.callFrame.leave();
      } catch (error) {
        console.error('Error leaving room:', error);
      }
    }
    this.callFrame = null;
    this.currentRoom = null;
    this.participants.clear();
  }

  /**
   * Toggle microphone
   * @param {boolean} muted - Mute state
   */
  async setMicrophoneEnabled(enabled) {
    if (this.callFrame) {
      try {
        await this.callFrame.setLocalAudio(enabled);
      } catch (error) {
        console.error('Error toggling microphone:', error);
      }
    }
  }

  /**
   * Toggle camera
   * @param {boolean} enabled - Video state
   */
  async setCameraEnabled(enabled) {
    if (this.callFrame) {
      try {
        await this.callFrame.setLocalVideo(enabled);
      } catch (error) {
        console.error('Error toggling camera:', error);
      }
    }
  }

  /**
   * Start screen sharing
   */
  async startScreenShare() {
    if (this.callFrame) {
      try {
        await this.callFrame.startScreenShare();
      } catch (error) {
        console.error('Error starting screen share:', error);
        throw error;
      }
    }
  }

  /**
   * Stop screen sharing
   */
  async stopScreenShare() {
    if (this.callFrame) {
      try {
        await this.callFrame.stopScreenShare();
      } catch (error) {
        console.error('Error stopping screen share:', error);
      }
    }
  }

  /**
   * Get current participants
   * @returns {Array} Participants array
   */
  getParticipants() {
    if (this.callFrame) {
      return this.callFrame.participants();
    }
    return [];
  }

  /**
   * Get local participant
   * @returns {Object} Local participant
   */
  getLocalParticipant() {
    if (this.callFrame) {
      return this.callFrame.participants().local;
    }
    return null;
  }

  /**
   * Set event handlers
   * @param {string} event - Event name
   * @param {Function} handler - Event handler
   */
  on(event, handler) {
    if (this.callFrame) {
      this.callFrame.on(event, handler);
      this.eventHandlers.set(event, handler);
    }
  }

  /**
   * Remove event handler
   * @param {string} event - Event name
   */
  off(event) {
    if (this.callFrame) {
      this.callFrame.off(event);
      this.eventHandlers.delete(event);
    }
  }

  /**
   * Cleanup all resources
   */
  destroy() {
    if (this.callFrame) {
      try {
        // Leave room first if joined
        if (this.currentRoom) {
          this.callFrame.leave().catch(err => console.warn('Error leaving room during cleanup:', err));
        }
        // Destroy the call frame
        this.callFrame.destroy();
      } catch (error) {
        console.warn('Error destroying call frame:', error);
      }
      this.callFrame = null;
    }
    this.currentRoom = null;
    this.participants.clear();
    this.eventHandlers.clear();
  }

  /**
   * Get room info
   * @param {string} roomName - Room name
   * @returns {Promise<Object>} Room info
   */
  async getRoomInfo(roomName) {
    if (!this.isConfigured()) {
      throw new Error('Daily.co API key not configured');
    }

    try {
      const response = await fetch(`https://api.daily.co/v1/rooms/${roomName}`, {
        headers: {
          'Authorization': `Bearer ${this.apiKey}`
        }
      });

      if (!response.ok) {
        throw new Error('Room not found');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting room info:', error);
      throw error;
    }
  }

  /**
   * Delete a room
   * @param {string} roomName - Room name
   */
  async deleteRoom(roomName) {
    if (!this.isConfigured()) {
      throw new Error('Daily.co API key not configured');
    }

    try {
      const response = await fetch(`https://api.daily.co/v1/rooms/${roomName}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete room');
      }
    } catch (error) {
      console.error('Error deleting room:', error);
      throw error;
    }
  }
}

// Export singleton instance
const dailyVideoService = new DailyVideoService();
export default dailyVideoService;

